<template>
    <div class="file-page">
        <page-header :title="title" :items="items" />

        <div v-if="file.id" class="row">
            <div class="col-xl-9 mb-3">
                <div class="card">
                    <div class="card-body">
                        <tabs
                            :items="sections"
                            :active-item="section"
                            class="mb-3"
                            @item-change="section = $event"
                        />
                        <info
                            v-if="section.value === 'general'"
                            :key="file.status"
                            v-model="file"
                            :validation="$v"
                            :server-errors="serverErrors"
                        />
                        <edit-form
                            v-if="section.value === 'stars'"
                            v-model="file"
                            :validation="$v"
                            :server-errors="serverErrors"
                            :user="file.user"
                        />
                        <comments
                            v-if="section.value === 'comments'"
                            :file-id="file.id"
                        />
                    </div>
                </div>
            </div>

            <div class="col-xl-3 mb-3">
                <div class="card">
                    <div class="card-body">
                        <div class="mb-3">
                            <button
                                v-if="file.status !== APPROVED"
                                v-b-modal.confirm-change-status-modal
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="currentStatus = APPROVED"
                            >
                                <i class="fas fa-check-circle" />
                                Approve
                            </button>
                            <button
                                v-if="file.status !== REJECTED"
                                v-b-modal.confirm-change-status-modal
                                class="btn btn-danger btn-static waves-effect waves-light m-1"
                                @click="currentStatus = REJECTED"
                            >
                                <i class="fa fa-times-circle" />
                                Reject
                            </button>

                            <button
                                class="btn btn-success btn-static waves-effect waves-light m-1"
                                @click="submitHandler"
                            >
                                <i class="fas fa-save" />
                                Save
                            </button>
                        </div>

                        <item-information
                            :edit-mode="true"
                            :sections="{ status: true }"
                            :item="file"
                            @on-delete-confirm="onDeleteConfirm"
                        />
                    </div>
                </div>
            </div>
        </div>
        <skeleton-loader v-else />

        <confirmation-modal
            id="confirm-change-status-modal"
            :title="`Are you sure you want to ${status} this file?`"
            @agree="changeStatus"
        />
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { required } from 'vuelidate/lib/validators';
import { StatusCodes as HTTP } from 'http-status-codes';
import Info from '@/components/files/Info';
import Tabs from '@/components/elements/Tabs';
import Comments from '@/components/files/Comments';
import EditForm from '@/components/files/EditForm';
import ItemInformation from '@/components/elements/ItemInformation';
import ConfirmationModal from '@/components/elements/ConfirmationModal';

export default {
    page: {
        title: 'Manage File'
    },

    validations: {
        file: {
            likesThreshold: {
                required
            },
            starsAmount: {
                required
            },
            year: {
                required
            },
            term: {
                required
            },
            type: {
                required
            },
            fileType: {
                required
            }
        }
    },

    components: {
        Tabs,
        Info,
        Comments,
        EditForm,
        ItemInformation,
        ConfirmationModal
    },

    data() {
        const { id = null } = this.$route.params;

        return {
            title: 'Manage file',
            items: [
                {
                    text: 'Aguda',
                    to: '/'
                },
                {
                    text: 'Files',
                    to: '/files'
                },
                {
                    text: 'Edit',
                    active: true
                }
            ],
            id,
            file: {},
            serverErrors: [],
            sections: [
                {
                    value: 'general',
                    text: 'General'
                },
                {
                    value: 'stars',
                    text: 'Stars'
                },
                { value: 'comments', text: 'Comments' }
            ],
            section: { value: 'general' },
            currentStatus: ''
        };
    },

    computed: {
        ...mapGetters({
            PENDING: 'files/STATUS_PENDING',
            APPROVED: 'files/STATUS_APPROVED',
            REJECTED: 'files/STATUS_REJECTED'
        }),

        status() {
            return this.currentStatus === this.APPROVED ? 'approve' : 'reject';
        }
    },

    async created() {
        try {
            this.file = await this.getFile(this.id);

            this.file.author = this.file.user;
        } catch (error) {
            console.error(error);

            this.$toasterError();
        }
    },

    methods: {
        ...mapActions({
            deleteFile: 'files/destroy',
            getFile: 'files/show',
            updateFile: 'files/update',
            manageStatus: 'files/status'
        }),

        async submitHandler() {
            this.serverErrors = [];

            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            try {
                const { updatedAt } = await this.updateFile({
                    id: this.id,
                    data: this.file
                });

                this.file.updatedAt = updatedAt;

                this.$toaster('File updated!');
            } catch (error) {
                const { response } = error;

                if (response) {
                    const { data, status } = response;

                    if (status === HTTP.BAD_REQUEST && data.errors) {
                        this.serverErrors = data.errors;

                        this.$toasterError('Recheck your form.');

                        return;
                    }
                }

                console.error(error);

                this.$toasterError();
            }
        },

        async onDeleteConfirm() {
            try {
                await this.deleteFile(this.id);

                this.$router.push('/files');
            } catch (error) {
                console.error(error);

                this.$toasterError();
            }
        },

        async changeStatus() {
            try {
                const data = {
                    status: this.currentStatus,
                    id: this.file.id
                };

                const { updatedAt } = await this.manageStatus(data);

                this.file = {
                    ...this.file,
                    updatedAt,
                    status: this.currentStatus
                };

                this.$toaster(`File has been ${this.currentStatus}`);
            } catch (error) {
                console.error(error);

                this.$toasterError();
            } finally {
                this.currentStatus = '';
            }
        }
    }
};
</script>
